import {
    AdminListingAnalytics,
    AdminListingReason,
    AdminListingSeverity,
    AdminMarketplaceType,
} from '../../../../gen/api/models';

/** How many products are fetched first, so the marketplace filter and "Show more" work without refetching. */
export const ATTENTION_FETCH_LIMIT = 200;
/** What "Show all" fetches when the first fetch may have cut the list: the API maximum. */
export const ATTENTION_FETCH_ALL_LIMIT = 2000;
/** How long "Resolve" hides a product from the widget, for all admins. */
export const RESOLVE_DAYS = 7;
/** Market data older than this is flagged as out of date. */
export const MARKET_DATA_STALE_HOURS = 48;

/** Parses an API money value: decimals come as strings. */
export function parseMoney(value?: string | number | null): number | undefined {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const num = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(num) ? num : undefined;
}

export function formatPrice(value?: string | number | null): string {
    const num = parseMoney(value);
    if (num === undefined) {
        return '—';
    }
    return `${num < 0 ? '-' : ''}$${Math.abs(num).toFixed(2)}`;
}

/** Formats a ratio (0.25) as a signed percent ("+25%"). */
export function formatPercent(value?: string | number | null): string {
    const num = parseMoney(value);
    if (num === undefined) {
        return '—';
    }
    const percent = Math.round(num * 100);
    return `${percent > 0 ? '+' : ''}${percent}%`;
}

/** "3h ago", "2d ago" from an ISO timestamp. */
export function formatAgo(value?: string | null, now: number = Date.now()): string {
    const time = value ? new Date(value).getTime() : NaN;
    if (!Number.isFinite(time)) {
        return '—';
    }
    const hours = Math.max(0, Math.floor((now - time) / (60 * 60 * 1000)));
    if (hours < 1) {
        return 'just now';
    }
    if (hours < 48) {
        return `${hours}h ago`;
    }
    return `${Math.floor(hours / 24)}d ago`;
}

export function hoursSince(value?: string | null, now: number = Date.now()): number | undefined {
    const time = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(time) ? (now - time) / (60 * 60 * 1000) : undefined;
}

export const SEVERITY_LABEL: Record<AdminListingSeverity, string> = {
    [AdminListingSeverity.ListingSeverityOverpriced]: 'Above market',
    [AdminListingSeverity.ListingSeverityNotCompetitive]: 'Not below market',
    [AdminListingSeverity.ListingSeverityBelowFloor]: 'Min payout too high',
    [AdminListingSeverity.ListingSeverityStale]: 'No sales',
    [AdminListingSeverity.ListingSeverityNoMarketData]: 'No market data',
    [AdminListingSeverity.ListingSeverityOk]: 'OK',
};

export const SEVERITY_DESCRIPTION: Record<AdminListingSeverity, string> = {
    [AdminListingSeverity.ListingSeverityOverpriced]: 'Our price with shipping is above the market price.',
    [AdminListingSeverity.ListingSeverityNotCompetitive]: 'Not above the market, but not 5% below it either.',
    [AdminListingSeverity.ListingSeverityBelowFloor]:
        'This market pays less than our minimum: selling at the market price brings less than Min payout. Lower Min payout to what the market gives, or sell it another way (another marketplace, a group sale).',
    [AdminListingSeverity.ListingSeverityStale]: 'The price is fine, but there were no sales for a long time.',
    [AdminListingSeverity.ListingSeverityNoMarketData]: 'No market data yet.',
    [AdminListingSeverity.ListingSeverityOk]: 'Priced in line.',
};

export const REASON_LABEL: Record<AdminListingReason, string> = {
    [AdminListingReason.ListingReasonNotCompetitive]: 'not below market',
    [AdminListingReason.ListingReasonBelowFloor]: 'min payout too high',
    [AdminListingReason.ListingReasonStale]: 'no sales',
    [AdminListingReason.ListingReasonMarketDropped]: 'market dropped',
};

export type SeverityTone = 'error' | 'warning' | 'muted' | 'success';

export function severityTone(severity: AdminListingSeverity): SeverityTone {
    switch (severity) {
        case AdminListingSeverity.ListingSeverityOverpriced:
            return 'error';
        case AdminListingSeverity.ListingSeverityNotCompetitive:
        case AdminListingSeverity.ListingSeverityBelowFloor:
            return 'warning';
        case AdminListingSeverity.ListingSeverityStale:
        case AdminListingSeverity.ListingSeverityNoMarketData:
            return 'muted';
        default:
            return 'success';
    }
}

/** Profit of a sale: payout (after fees and shipping) minus the purchase price. */
export function profit(payout?: string | number | null, purchasePrice?: string | number | null): number | undefined {
    const p = parseMoney(payout);
    const cost = parseMoney(purchasePrice);
    return p === undefined || cost === undefined ? undefined : p - cost;
}

/** Whether our price should change: the suggested price differs by at least a cent. */
export function priceChangeSuggested(ourPrice?: string | number, suggestedPrice?: string | number | null): boolean {
    const our = parseMoney(ourPrice);
    const suggested = parseMoney(suggestedPrice);
    return our !== undefined && suggested !== undefined && Math.abs(our - suggested) >= 0.01;
}

/** The market below this share of MSRP means MSRP is a misleading anchor for the used price. */
export const MSRP_MARKET_WARN_RATIO = 0.5;

/** How our expectations for a product compare with what its market gives. */
export type ExpectationCheck = {
    /** The highest payout among listings at the suggested (market) prices. */
    bestPayout?: {value: number; marketplace: AdminMarketplaceType};
    /** Min payout is more than any marketplace gives at the market price. */
    minPayoutTooHigh: boolean;
    /** The eBay market price (buyer total), or another own market when there is no eBay listing. */
    marketPrice?: number;
    /** marketPrice as a share of MSRP (0.21 = 21%). */
    marketToMsrp?: number;
    /** The market is below MSRP_MARKET_WARN_RATIO of MSRP. */
    msrpMisleading: boolean;
};

export function checkExpectations(
    listings: AdminListingAnalytics[],
    msrp?: string | number | null,
    lowestResalePrice?: string | number | null
): ExpectationCheck {
    let bestPayout: ExpectationCheck['bestPayout'];
    let marketPrice: number | undefined;
    for (const listing of listings) {
        const payout = parseMoney(listing.suggestedNet);
        if (payout !== undefined && (bestPayout === undefined || payout > bestPayout.value)) {
            bestPayout = {value: payout, marketplace: listing.marketplace};
        }
        const reference = parseMoney(listing.referencePrice);
        if (reference === undefined || listing.referenceSource === 'derived_from_ebay') {
            continue;
        }
        if (listing.marketplace === AdminMarketplaceType.MarketplaceTypeEbay || marketPrice === undefined) {
            marketPrice = reference;
        }
    }

    const minPayout = parseMoney(lowestResalePrice) ?? 0;
    const retail = parseMoney(msrp) ?? 0;
    const marketToMsrp = marketPrice !== undefined && retail > 0 ? marketPrice / retail : undefined;
    return {
        bestPayout,
        minPayoutTooHigh: minPayout > 0 && bestPayout !== undefined && minPayout > bestPayout.value + 0.005,
        marketPrice,
        marketToMsrp,
        msrpMisleading: marketToMsrp !== undefined && marketToMsrp < MSRP_MARKET_WARN_RATIO,
    };
}

/** "21%" for a 0..1 share. */
export function formatShare(value?: number): string {
    return value === undefined ? '—' : `${Math.round(value * 100)}%`;
}
